import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAppDispatch } from '@app/store/hooks';
import { Button } from '@shared/ui/button/Button';
import { ErrorState } from '@shared/ui/state/ErrorState';
import { Icon } from '@shared/ui/icon/Icon';
import { paths } from '@shared/config/paths';
import { analyzeReceipt } from '../api/receiptsApi';
import { FilePreview } from '../components/FilePreview';
import { ReceiptDropzone } from '../components/ReceiptDropzone';
import { ReceiptForm } from '../components/ReceiptForm';
import { Stepper } from '../components/Stepper';
import { createReceipt } from '../store/receiptsSlice';
import type { ReceiptExtraction } from '../types';
import type { ReceiptFormValues } from '../schemas/receiptSchema';

const STEPS = ['Upload', 'Preview', 'Analyze', 'Review', 'Validate'] as const;

type SelectedFile = {
  file: File;
  previewUrl: string;
};

type AnalysisState =
  | { status: 'idle' }
  | { status: 'analyzing' }
  | { status: 'success'; extraction: ReceiptExtraction }
  | { status: 'error'; message: string };

/**
 * Parcours d'ajout : Upload → Aperçu → Analyse OCR → Revue/édition → Validation.
 * Le ticket validé est persisté dans le store Redux puis affiché dans la liste.
 */
export function AddReceiptPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisState>({ status: 'idle' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Libère l'URL objet quand le fichier change ou que la page est quittée.
  useEffect(() => {
    return () => {
      if (selectedFile) URL.revokeObjectURL(selectedFile.previewUrl);
    };
  }, [selectedFile]);

  const handleFileAccepted = useCallback((file: File) => {
    setFileError(null);
    setAnalysis({ status: 'idle' });
    setSelectedFile({ file, previewUrl: URL.createObjectURL(file) });
  }, []);

  const handleFileRejected = useCallback((message: string) => {
    setFileError(message);
  }, []);

  const runAnalysis = useCallback(async () => {
    if (!selectedFile) return;
    setAnalysis({ status: 'analyzing' });
    try {
      const extraction = await analyzeReceipt(selectedFile.file);
      setAnalysis({ status: 'success', extraction });
    } catch (error) {
      setAnalysis({
        status: 'error',
        message: error instanceof Error ? error.message : "Une erreur inattendue s'est produite.",
      });
    }
  }, [selectedFile]);

  const handleValidation = useCallback(
    async (values: ReceiptFormValues) => {
      if (!selectedFile) return;
      setSaving(true);
      setSaveError(null);
      try {
        // Persiste via l'API ; le serveur renvoie le receipt complet (id, statut, dates).
        await dispatch(
          createReceipt({
            ...values,
            fileName: selectedFile.file.name,
            fileType: selectedFile.file.type,
          }),
        ).unwrap();
        void navigate(paths.receipts);
      } catch (error) {
        setSaveError(
          error instanceof Error ? error.message : "Impossible d'enregistrer le ticket.",
        );
      } finally {
        setSaving(false);
      }
    },
    [dispatch, navigate, selectedFile],
  );

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setFileError(null);
    setAnalysis({ status: 'idle' });
  }, []);

  // Étape courante du stepper, dérivée de l'état du parcours.
  const currentStep = !selectedFile
    ? 0 // Upload
    : analysis.status === 'idle'
      ? 1 // Preview
      : analysis.status === 'success'
        ? 3 // Review
        : 2; // Analyze (en cours ou en erreur)

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <div>
          <Link
            to={paths.receipts}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            <Icon name="arrow-left" size={16} />
            Retour à la liste
          </Link>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Ajouter un ticket</h1>
        <p className="text-sm text-slate-500">
          Importez un document, lancez l'analyse puis vérifiez les données avant d'enregistrer.
        </p>
      </div>

      <Stepper steps={STEPS} current={currentStep} />

      {/* Étape 1 — Upload */}
      {!selectedFile && (
        <div className="flex flex-col gap-3">
          <ReceiptDropzone
            onFileAccepted={handleFileAccepted}
            onFileRejected={handleFileRejected}
          />
          {fileError ? (
            <p role="alert" className="text-sm text-red-600">
              {fileError}
            </p>
          ) : null}
        </div>
      )}

      {/* Étapes 2 à 4 — Aperçu, analyse, revue */}
      {selectedFile && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <FilePreview
              fileName={selectedFile.file.name}
              fileType={selectedFile.file.type}
              previewUrl={selectedFile.previewUrl}
            />
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="sm" onClick={handleReset}>
                Changer de document
              </Button>
              {analysis.status !== 'success' && (
                <Button onClick={runAnalysis} disabled={analysis.status === 'analyzing'}>
                  {analysis.status === 'analyzing' ? (
                    <>
                      <Icon name="spinner" size={16} />
                      Analyse en cours…
                    </>
                  ) : (
                    <>
                      <Icon name="sparkles" size={16} />
                      Analyser le document
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          <div>
            {analysis.status === 'analyzing' && (
              <div
                role="status"
                className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-8 py-12"
              >
                {/* Processing indicator */}
                <div className="relative flex h-12 w-12 items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-2 border-slate-100" />

                  <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-emerald-500" />

                  <Icon name="sparkles" size={19} className="text-emerald-600" />
                </div>

                <div className="mt-5 text-center">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Analyse du document en cours
                  </h3>

                  <p className="mt-1.5 text-xs text-slate-500">
                    Extraction des informations clés et préparation de votre reçu
                  </p>
                </div>

                {/* Processing steps */}
                <div className="mt-7 flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5 text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Lecture
                  </span>

                  <span className="text-slate-300">/</span>

                  <span className="flex items-center gap-1.5 text-slate-500">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-300" />
                    Extraction
                  </span>

                  <span className="text-slate-300">/</span>

                  <span className="flex items-center gap-1.5 text-slate-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-200" />
                    Structuration
                  </span>
                </div>
              </div>
            )}

            {analysis.status === 'error' && (
              <div className="flex h-full min-h-[420px] items-center">
                <div className="w-full">
                  <ErrorState
                    title="L'analyse a échoué"
                    message={analysis.message}
                    action={
                      <Button variant="secondary" onClick={runAnalysis}>
                        Réessayer l'analyse
                      </Button>
                    }
                  />
                </div>
              </div>
            )}

            {analysis.status === 'success' && (
              <div className="min-h-[420px]">
                {saveError ? (
                  <p role="alert" className="mb-3 text-sm text-red-600">
                    {saveError}
                  </p>
                ) : null}

                <ReceiptForm
                  extraction={analysis.extraction}
                  onSubmit={handleValidation}
                  onCancel={() => void navigate(paths.receipts)}
                  submitting={saving}
                />
              </div>
            )}

            {analysis.status === 'idle' && (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <span className="flex size-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <Icon name="sparkles" size={22} />
                </span>

                <p className="mt-3 text-sm font-medium text-slate-700">
                  Document prêt à être analysé
                </p>

                <p className="text-xs text-slate-500">
                  Lancez l'analyse pour préremplir le formulaire.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
